import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemFieldsAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';

import type { genericField_updateWorkItemFieldsActionRenderer$key } from './__generated__/genericField_updateWorkItemFieldsActionRenderer.graphql';
import { sanitizeFieldName, sanitizeFieldValue } from './utils';

// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable-next-line no-barrel-files/no-barrel-files */
export type { genericField_updateWorkItemFieldsActionRenderer$key as GenericFieldType };

const messages = defineMessages({
	updateAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.generic-field.updateAction',
		defaultMessage: 'Update {fieldName} to {fieldValue}',
		description: 'Label for the change field field',
	},
	clearAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.generic-field.clearAction',
		defaultMessage: 'Clear {fieldName}',
		description:
			'Accessible label for the button that clears the current value of a generic field in a Jira work item action card.',
	},
});

export type Props = {
	queryRef: genericField_updateWorkItemFieldsActionRenderer$key;
	action: UpdateWorkItemFieldsAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const GenericField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment genericField_updateWorkItemFieldsActionRenderer on JiraIssueField {
				fieldId
				name
			}
		`,
		queryRef,
	);

	const isFieldRenderable = data.fieldId === action?.data?.fieldKey;
	const isUpdateAction = isFieldRenderable && !!action?.data?.fieldValue;

	const formattedMessage = isUpdateAction
		? formatMessage(messages.updateAction, {
				fieldName: sanitizeFieldName(data?.name),
				fieldValue: sanitizeFieldValue(action?.data?.fieldValue),
			})
		: formatMessage(messages.clearAction, {
				fieldName: sanitizeFieldName(data?.name),
			});

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={action?.data?.reasoning}
				fieldName={data?.name}
				suggested={
					action?.data?.fieldValue ? sanitizeFieldValue(action.data.fieldValue) : undefined
				}
			/>
		),
		[action.data.fieldValue, action.data?.reasoning, data?.name],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: action?.data?.fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!isFieldRenderable || !data?.name) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.generic-field"
		>
			{formattedMessage}
		</Text>
	);
};
